import {IconButton, Tab, Tabs, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../../components/common/arr-tooltip-wrapper/ArrTooltipWrapper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './AssignUsers.scss';
import {ReactNode, useState} from 'react';
import AssignUsersContent from './AssignUsersContent';
import ArrModal from '../../../components/common/arr-modal/ArrModal';
import {ArrDrawer} from '../../../components/common/arr-drawer/ArrDrawer';
import ArrToast from '../../../components/common/arr-toast/ArrToast';
import {ReactComponent as CloseToast} from '../../../../src/assets/images/close-toast.svg';
import {TOAST_DURATION} from '../../../shared/constants';
import {
  useManageUsers,
  useUpdateDomainUsers,
  useUpdateProjectUsers,
} from '../../../hooks/Projects/useProjects';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  setClearState,
  setClearTabData,
} from '../../../redux/slice/AssignUsersSlice';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  className?: string;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      data-testid={`assign-users-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`assign-users-chil-tab-${index}`}>{children}</div>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
    name: `tab-${index}`,
    'data-testid': `tab-${index}`,
  };
}

interface TabData {
  label: string;
  content: React.ReactNode;
}

interface IAssignUsers {
  open: boolean;
  handleDrawer: (open: boolean) => void;
  onUsersAssigned?: () => void;
}

const AssignUsers: React.FC<IAssignUsers> = ({
  open,
  handleDrawer,
  onUsersAssigned,
}) => {
  const dispatch = useDispatch();
  const domain = useSelector((state: RootState) => state.assignUser.domain);
  const project = useSelector((state: RootState) => state.assignUser.project);
  const user = useSelector((state: RootState) => state.authenticatedUser.user);
  const {data: manageUsersData, isLoading} = useManageUsers(
    user?.projectId ?? 0
  );
  const {
    mutate: updateDomainUsers,
    isError: domainError,
    error: domainUsersError,
  } = useUpdateDomainUsers();
  const {
    mutate: updateProjectUsers,
    isError: projectError,
    error: projectUsersError,
  } = useUpdateProjectUsers();
  const [value, setValue] = useState(0);
  const [showToast, setShowToast] = useState<boolean>(false);

  const saveDisable =
    domain.dropdownId === null && project.projectDropdownId === null;

  const tabData: TabData[] = [
    {
      label: t('userManagement.assignUsers.domains'),
      content: (
        <AssignUsersContent
          title={t('userManagement.assignUsers.selectingDomain')}
          type={t('userManagement.assignUsers.domain')}
          manageUsersData={manageUsersData ?? null}
          isLoader={isLoading}
        />
      ),
    },
    {
      label: t('userManagement.assignUsers.projects'),
      content: (
        <AssignUsersContent
          title={t('userManagement.assignUsers.selectingProject')}
          type={t('userManagement.assignUsers.project')}
          manageUsersData={manageUsersData ?? null}
          isLoader={isLoading}
        />
      ),
    },
  ];

  const [unsavedChangesModal, setUnsavedChangesModal] =
    useState<boolean>(false);
  const handleCancel = () => {
    if (saveDisable) {
      handleDrawer(false);
    } else {
      setUnsavedChangesModal(true);
    }
  };

  const handleSave = () => {
    if (domain?.dropdownId != null) {
      const assignedUserList = domain?.assignedUsers?.map((item) => {
        const [userIdStr, isBillableStr] = item.value.split('|||');
        return {
          userId: Number(userIdStr),
          isBillable: isBillableStr === 'true',
        };
      });
      const unAssignedUserList =
        domain?.unAssginedUsers?.map((item) => ({
          userId: item.key ?? 0,
          isBillable: false,
        })) || [];

      const domainPayload = {
        createdBy: 1,
        assignedUserList,
        unAssignedUserList,
        selectedEntityID: Number(domain?.dropdownId?.id),
      };
      updateDomainUsers(domainPayload, {
        onSuccess: () => {
          clearStoreValues();
          dispatch(setClearState(true));
          setShowToast(true);

          // Trigger grid refresh if callback provided
          if (onUsersAssigned) {
            onUsersAssigned();
          }
        },
        onError: () => {
          setShowToast(true);
        },
      });
      return;
    }
    if (project.projectDropdownId != null) {
      const projectAssignedUser = project?.assignedUsers.map(({value}) => {
        const [userIdStr, role, securityGroupIDs, isBillableStr] =
          value.split('|');
        return {
          userId: Number(userIdStr),
          role,
          newSecurityGroupIDs: securityGroupIDs,
          isBillable: isBillableStr === 'true',
        };
      });

      const projectPayload = {
        assignedUserList: projectAssignedUser,
        projectID: Number(project?.projectDropdownId?.id),
        createdBy: 1,
      };
      updateProjectUsers(projectPayload, {
        onSuccess: () => {
          clearStoreValues();
          dispatch(setClearState(true));
          setShowToast(true);

          // Trigger grid refresh if callback provided
          if (onUsersAssigned) {
            onUsersAssigned();
          }
        },
        onError: () => {
          setShowToast(true);
        },
      });
      return;
    }
    // If nothing to save, just close drawer
    handleDrawer(false);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleNoDontSave = () => {
    clearStoreValues();
    setUnsavedChangesModal(false);
    handleDrawer(false);
  };

  const clearStoreValues = () => {
    dispatch(setClearTabData('domain'));
    dispatch(setClearTabData('project'));
  };

  return (
    <ArrDrawer
      showButton={true}
      isOpen={open}
      size="large"
      side="right"
      onClose={handleCancel}
    >
      <div
        className="assign-users-container"
        data-testid={`assign-users-assign-users-assign-users-info-cancel-save`}
      >
        <div
          className="assign-users-action"
          data-testid={`assign-users-assign-users-assign-users-info-cancel-save-btn`}
        >
          <div
            className="assign-users-label"
            data-testid={`assign-users-assign-users-assign-users-info`}
          >
            <Typography
              className="typo-text headline3_size assign-users-title"
              data-testid={`assign-users-assign-users`}
            >
              {t('userManagement.assignUsers.assignUsers')}
            </Typography>
            <ArrTooltipWrapper
              trigger={
                <IconButton aria-label="Info" sx={{padding: 0}}>
                  <InfoOutlinedIcon fontSize="medium" className="info-icon" />
                </IconButton>
              }
            >
              {t('userManagement.assignUsers.assignUsersInfo')}
            </ArrTooltipWrapper>
          </div>
          <ArrButton
            variant="secondary"
            size="small"
            onClick={handleCancel}
            dataTestId="assign-users-cancel-button"
            className="cancel-button"
            name={t('userManagement.assignUsers.cancel')}
            id={t('userManagement.assignUsers.cancel')}
          >
            {t('userManagement.assignUsers.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="small"
            onClick={handleSave}
            dataTestId="save-button"
            className="save-button"
            disabled={saveDisable}
            name={t('userManagement.assignUsers.save')}
            id={t('userManagement.assignUsers.save')}
          >
            {t('userManagement.assignUsers.save')}
          </ArrButton>
        </div>
        {user?.roleId === -1 ? (
          <div className="tab-header" data-testid={`assign-users-div-button`}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="assign user tabs"
              className="assign-user-tabs"
            >
              {tabData.map((tab, index) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  className="assign-user-tab"
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>

            {tabData.map((tab, index) => (
              <TabPanel key={tab.label} value={value} index={index}>
                {tab.content}
              </TabPanel>
            ))}
          </div>
        ) : (
          <AssignUsersContent
            title={t('userManagement.assignUsers.selectingProject')}
            type={t('userManagement.assignUsers.project')}
            manageUsersData={manageUsersData ?? null}
            isLoader={isLoading}
          />
        )}
      </div>
      {unsavedChangesModal && (
        <ArrModal
          showModal={unsavedChangesModal}
          showClose={false}
          onClose={() => setUnsavedChangesModal(false)}
          title={t('userManagement.assignUsers.unsavedTitle')}
          description={t('userManagement.assignUsers.unsavedDescription')}
          dataTestId="usermanagement-assignusers-modal"
          buttons={[
            {
              id: '1',
              label: t('userManagement.assignUsers.noDontSave'),
              onClick: () => handleNoDontSave(),
              variant: 'secondary',
              dataTestId: 'uma-nosave',
            },
            {
              id: '2',
              label: t('userManagement.assignUsers.saveChanges'),
              onClick: () => setUnsavedChangesModal(false),
              variant: 'primary',
              dataTestId: 'uma-save',
            },
          ]}
        />
      )}
      {showToast && (
        <ArrToast
          id="toast-1"
          title={domainError || projectError ? 'Error' : 'Success'}
          message={
            domainUsersError?.message ||
            projectUsersError?.message ||
            t('userManagement.assignUsers.assignUsersSaved')
          }
          type={domainError || projectError ? 'error' : 'success'}
          duration={TOAST_DURATION}
          onClose={() => setShowToast(false)}
          dismissable={true}
          closeIcon={<CloseToast />}
          position="bottom-right"
          className="assign-activation-toast"
        />
      )}
    </ArrDrawer>
  );
};

export default AssignUsers;
